import type { JSX } from 'react';
import { useEffect, useState, lazy, Suspense, type ReactNode } from 'react';
import { api, ApiError } from '../api/client';
import { reportColdOpen } from '../lib/cold-open-report';
import { prefetchOwnerFirstPage } from '../lib/owner-conversation-client';
const OfflineOwnerConversation = lazy(() => import('./OwnerConversationPage').then(m => ({ default: m.OwnerConversationPage })));
const offlineOwnerKey = 'td-owner-authenticated-once';
function rememberOwnerAccess(value: boolean): void { try { if (value) localStorage.setItem(offlineOwnerKey, '1'); else localStorage.removeItem(offlineOwnerKey); } catch { /* Falha local não autoriza modo offline. */ } }
/**
 * Quanto a verificação de sessão pode demorar antes de a conversa guardada no aparelho aparecer. Abertura a frio do app
 * do dono (17/10): o servidor chegou a levar de 17 s a 82 s e a tela ficava em "carregando…" esperando o /api/auth-info,
 * mesmo com a conversa já no IndexedDB. A verificação continua valendo: negada, a tela de login substitui a conversa.
 */
export const OWNER_CACHE_AFTER_MS = 1200;
function canShowCachedOwner(): boolean { try { return location.pathname === '/donizeti' && localStorage.getItem(offlineOwnerKey) === '1'; } catch { return false; } }
/** A página foi aberta pelo próprio Lenovo; quem decide se é loopback continua sendo o servidor (`info.loopback`). */
function onLoopbackHost(): boolean { return ['127.0.0.1', 'localhost', '[::1]', '::1'].includes(location.hostname); }
function rememberedOwner(): boolean { try { return localStorage.getItem(offlineOwnerKey) === '1'; } catch { return false; } }
function canReadOfflineOwner(): boolean { try { return location.pathname === '/donizeti' && !navigator.onLine && localStorage.getItem(offlineOwnerKey) === '1'; } catch { return false; } }

/**
 * Portao de login (UI remota). No LOOPBACK, obtem o cookie de UI direto
 * (`ensureUiSession`) e renderiza o app — sem fricçao, sem 2FA (loopback e trusted, M6.3).
 * No REMOTO (Tailscale), entra direto quando o cookie td_ui atual ja autentica
 * (`authenticated`); so mostra a tela de login quando ha senha habilitada E ainda nao ha
 * sessao-de-UI valida. Quando o 2FA esta ativo, a tela tambem pede o codigo (TOTP/backup).
 *
 * Estados:
 *  - loading: consultando /api/auth-info
 *  - ready: loopback, cookie remoto ja valido (authenticated) ou login bem-sucedido -> children
 *  - login: remoto com senha habilitada e SEM cookie valido -> formulario (senha [+ codigo 2FA])
 *  - blocked: remoto sem senha habilitada e sem cookie -> instrui usar token (ou habilitar senha)
 */
export function LoginGate({ children }: { children: ReactNode }): JSX.Element {
  const [phase, setPhase] = useState<'loading' | 'ready' | 'login' | 'blocked' | 'owner-offline'>('loading');
  // M452.2 — a causa de o portal estar pedindo credencial, e por quanto tempo a proxima sessao vale.
  const [causa, setCausa] = useState<string | null>(null);
  const [duracaoDias, setDuracaoDias] = useState<number | null>(null);
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [needsTotp, setNeedsTotp] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const startedAt = performance.now();
    let authInfoMs: number | null = null, cachedShownMs: number | null = null, reported = false, cachedShown = false;
    const settle = (next: 'ready' | 'login' | 'blocked' | 'owner-offline'): void => {
      setPhase(next);
      if (!reported && next !== 'owner-offline') { reported = true; reportColdOpen({ authInfoMs, cachedShownMs, gateMs: Math.round(performance.now() - startedAt), outcome: next }); }
    };
    // Servidor lento: mostra a conversa guardada sem esperar o auth-info (só para quem já entrou aqui antes).
    const slowTimer = canShowCachedOwner() ? setTimeout(() => {
      cachedShown = true; cachedShownMs = Math.round(performance.now() - startedAt);
      setPhase(current => current === 'loading' ? 'owner-offline' : current);
    }, OWNER_CACHE_AFTER_MS) : undefined;
    // O código da conversa e a janela recente saem em paralelo à verificação (f963502b c): quando o gate libera, a página
    // já tem o que pintar. Nada aparece antes do gate; a janela recente só é pedida por quem já entrou aqui ou pelo
    // próprio Lenovo, para não gerar leitura anônima recusada.
    if (location.pathname === '/donizeti') {
      void import('./OwnerConversationPage').catch(() => undefined);
      if (onLoopbackHost() || rememberedOwner()) prefetchOwnerFirstPage();
    }
    const verify = async () => {
      // No Lenovo o cookie de UI é pedido junto com o auth-info; ele só é usado se o servidor confirmar o loopback.
      const earlySession = onLoopbackHost() ? Promise.resolve().then(() => api.ensureUiSession()) : null; earlySession?.catch(() => undefined);
      try {
        const asked = performance.now();
        const info = await api.authInfo();
        authInfoMs ??= Math.round(performance.now() - asked);
        clearTimeout(slowTimer);
        if (info.loopback) {
          // Local: obtem o cookie e segue (loopback dispensa 2FA — trusted).
          await (earlySession ?? api.ensureUiSession());
          rememberOwnerAccess(true);
          settle('ready');
        } else if (info.authenticated) {
          rememberOwnerAccess(true);
          // Remoto, mas o cookie td_ui atual ja e valido -> nao re-pede senha no refresh.
          settle('ready');
        } else if (info.loginEnabled) {
          rememberOwnerAccess(false);
          setNeedsTotp(info.twoFactorEnabled);
          // O texto e escrito na voz de quem le, e cada causa pede uma reacao diferente: expirou por tempo
          // nao e o mesmo que revogado (que sugere um ato) nem que corrompido (que sugere um problema).
          setCausa(
            info.sessionState === 'expired'
              ? 'sua sessao anterior expirou por tempo'
              : info.sessionState === 'revoked'
                ? 'as sessoes foram encerradas neste servidor'
                : info.sessionState === 'malformed'
                  ? 'o dado de sessao deste navegador esta ilegivel'
                  : 'este navegador ainda nao tem sessao aqui',
          );
          if (typeof info.sessionTtlSec === 'number' && info.sessionTtlSec > 0) {
            setDuracaoDias(Math.round(info.sessionTtlSec / 86_400));
          }
          settle('login');
        } else {
          settle('blocked');
        }
      } catch {
        clearTimeout(slowTimer);
        // Somente a conversa já sincronizada: não abre a frota nem concede autoridade de servidor.
        // Qualquer reconexão volta a validar o cookie atual antes de renderizar o portal.
        // Falha (não negação) com a conversa guardada já à vista: ela continua, em vez de trocar por um login enganoso.
        if (canReadOfflineOwner() || cachedShown) { settle('owner-offline'); return; }
        // Falha do /api/auth-info (servidor antigo, ou BLIP DE REDE no remoto). NAO seta 'ready'
        // cego (M8-WEB#33): no remoto isso mostraria o app "logado" mas quebrado (cascata de 403).
        // `ensureUiSession` so funciona no LOOPBACK (remoto -> 403). Se ela passar, e loopback ->
        // ready; se falhar, e remoto (ou rede instavel) -> tela de login (nao o app quebrado).
        try {
          await api.ensureUiSession();
          settle('ready');
        } catch {
          settle('login');
          setErr('falha ao verificar a sessao — informe a senha para continuar');
        }
      }
    };
    const online = () => { if (location.pathname === '/donizeti') void verify(); };
    window.addEventListener('online', online); void verify();
    return () => { clearTimeout(slowTimer); window.removeEventListener('online', online); };
  }, []);

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await api.login(password, needsTotp ? totp.trim() : undefined);
      rememberOwnerAccess(true);
      setPhase('ready');
    } catch (ex) {
      const code = ex instanceof ApiError ? ex.code : undefined;
      if (code === 'totp_required') {
        // O servidor exige o segundo fator (caso o auth-info estivesse desatualizado).
        setNeedsTotp(true);
        setErr('informe tambem o codigo de 6 digitos do autenticador');
      } else if (code === 'totp_invalid') {
        setErr('codigo de autenticacao invalido');
      } else if (code === 'locked_out' || code === 'rate_limited' || /429|rate/.test(String(ex))) {
        setErr('muitas tentativas — aguarde antes de tentar de novo');
      } else {
        setErr('senha incorreta');
      }
    } finally {
      setBusy(false);
    }
  };

  if (phase === 'ready') {
    return <>{children}</>;
  }
  if (phase === 'owner-offline') return <Suspense fallback={<div className="gate">Abrindo conversa guardada no aparelho…</div>}><OfflineOwnerConversation /></Suspense>;
  if (phase === 'loading') {
    return (
      <div className="gate">
        <div className="gate-card">carregando…</div>
      </div>
    );
  }
  if (phase === 'blocked') {
    return (
      <div className="gate">
        <div className="gate-card">
          <span className="brand-mark">▟</span>
          <h1>term-deck</h1>
          <p className="gate-msg">
            Acesso remoto requer autenticacao. Defina <code>TERM_DECK_UI_PASSWORD</code> no servidor
            para habilitar login por senha, ou use um token Bearer.
          </p>
        </div>
      </div>
    );
  }
  return (
    <div className="gate">
      <form className="gate-card" onSubmit={(e) => void submit(e)}>
        <span className="brand-mark">▟</span>
        <h1>term-deck</h1>
        <p className="gate-msg">
          acesso remoto — informe a senha{needsTotp ? ' e o codigo do autenticador' : ''}
        </p>
        {causa && (
          <p className="gate-cause">
            {causa}
            {duracaoDias !== null && ` · a nova vale ${duracaoDias} dias e se renova com o uso`}
          </p>
        )}
        <input
          className="gate-input"
          type="password"
          placeholder="senha"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoFocus
        />
        {needsTotp && (
          <input
            className="gate-input"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="codigo 2FA (6 digitos) ou backup"
            value={totp}
            onChange={(e) => setTotp(e.target.value)}
          />
        )}
        {err && <div className="gate-err">{err}</div>}
        <button
          type="submit"
          className="btn-launch gate-btn"
          disabled={busy || !password || (needsTotp && !totp.trim())}
        >
          entrar
        </button>
      </form>
    </div>
  );
}
